// -----------------------------------------------------------------------------
// Gridline setup (v0.7.0) — boards A6.4 (detected gridlines), A6.4.1 (copy: choose component),
// A6.4.3 (copy preview + "Replace existing gridlines?"), A6.5 (zones on a multi-block floor).
// Opened from the A6 Grid column (Project setup) and from A5.2 "Adjust grid".
//
//   Stage      the plan page (pdf.js) with the lines drawn over it in an SVG of the same size. The stage is moved,
//              turned (⟲ ⟳, view only) and zoomed with one CSS transform; toStage() turns a pointer position back into
//              page coordinates (0–1), so every tool works the same whichever way the view is turned.
//   Tools      Select (click a line; drag it; drag an end dot = longer / shorter, Shift = change its angle),
//              Add line (drag), Draw zone (click the corners, click the first one again or double-click to close).
//              Dragging empty space pans in every tool. Delete removes the selected line; Ctrl+Z / Ctrl+Y undo / redo.
//   Detection  grid.ts detectGrid on pdf.geometry(page) — runs by itself when the component has no grid yet;
//              Re-detect replaces the lines (undo brings them back). Amber = no label or flagged to check.
//   Copy       A6.4.1 picks a component whose grid is finished; its lines show as a blue dashed preview that can be
//              dragged into place; Confirm copy (A6.4.3 asks first when lines exist) replaces lines + zones.
//   Save       issues_grid_save (0016, Project setup only) with the page ratio; Cancel / × / Esc ask when unsaved.
//   v0.7.1     Ctrl + wheel zooms the plan (core/wheelZoom.ts), a plain wheel pans; the pointer shows move over a line and a
//              hand elsewhere; while a zone's corners are adjusted nothing else can be picked (Done ends it); a cursor in a
//              zone without its own lines reads the main grid (grid.ts); a plan turned at upload is drawn and read turned.
// -----------------------------------------------------------------------------
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import { rpc } from "../../../core/api/backend";
import { useRpc } from "../../../core/api/useRpc";
import { useEsc } from "../../../core/esc";
import { ErrorBox, InfoIcon, Loading, toast, useAskClose } from "../../../core/ui/kit";
import type { Grid, GridLine, GridZone, PlanRef, PlansInfo } from "../../../core/api/types";
import { useWheelZoom } from "../../../core/wheelZoom";
import { copyGrid, detectGrid, DETECT_DEFAULTS, gridAngle, gridCounts, isAmber, lineAngle, linesInZone, pinReading, setAngle, type DetectOptions, type PageGeometry } from "../grid";
import { useOpenPdf } from "./Plan";
import { Icon } from "../../../core/ui/icons";

export const GRID_TIPS = {
  toolbar: ["Drag empty space to pan.", "Ctrl + mouse wheel zooms the plan (not the browser)."],
  deleteLine: "Shift + drag an end dot to change the line's angle.",
  minLength: "Skip lines shorter than this share of the sheet's shorter side.",
  joinGap: ["Dashed or broken gridlines are joined when the gaps are smaller than this.", "1 pt ≈ 0.35 mm."],
  noZones: "Without zones, the whole sheet uses one grid.",
  zones: "Pins read the grid of the zone they land in, e.g. TB · C/4–5.",
  copyFrom: ["Only components with a finished grid can be picked.", "After Apply you'll see the copied lines on this plan before anything changes."],
  preview: ["Drag the blue lines to line them up.", "Confirming replaces this plan's gridlines with the copy.", "Ctrl+Z undoes it."],
  replace: ["Pins already placed keep their position.", "Their gridline readings are worked out again from the new lines."],
};
const ZONE_COLOURS = ["#3B82F6", "#8B5CF6", "#0EA5E9", "#14B8A6", "#EC4899", "#F59E0B"];
type Tool = "select" | "add" | "zone";
interface State { lines: GridLine[]; zones: GridZone[] }
interface Preview { lines: GridLine[]; zones: GridZone[]; from: { prefix: string; name: string; file: string }; dx: number; dy: number }
type Drag =
  | { kind: "pan"; sx: number; sy: number; px: number; py: number }
  | { kind: "move"; id: string; u: number; v: number; orig: GridLine; moved: boolean }
  | { kind: "end"; id: string; end: 1 | 2; orig: GridLine }
  | { kind: "new"; id: string; u: number; v: number }
  | { kind: "corner"; zone: string; k: number }
  | { kind: "copy"; u: number; v: number; dx: number; dy: number };
let idSeq = 0;
const newId = (p: string) => `${p}${Date.now().toString(36)}${idSeq++}`;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export function GridSetupModal({ code, prefix, onClose, onSaved }: { code: string; prefix: string; onClose: () => void; onSaved: () => void }) {
  const q = useRpc("issues_grid_get", { code, prefix });
  if (!q.data) return (
    <div className="modal-backdrop"><div className="modal grid-modal" role="dialog" aria-label="Gridline setup">
      <div className="modal-head"><h2>Gridline setup</h2><button className="btn icon" aria-label="Close" onClick={onClose}><Icon name="close" /></button></div>
      {q.error ? <ErrorBox message={q.error} /> : <Loading />}</div></div>);
  if (!q.data.plan) return (
    <div className="modal-backdrop"><div className="modal grid-modal small" role="dialog" aria-label="Gridline setup">
      <div className="modal-head"><h2>Gridline setup</h2><button className="btn icon" aria-label="Close" onClick={onClose}><Icon name="close" /></button></div>
      <p className="muted pad">Upload a plan for {prefix} first (Project setup › Components › Plan).</p></div></div>);
  return <GridEditor code={code} prefix={prefix} name={q.data.name} plan={q.data.plan} grid={q.data.grid} canEdit={q.data.can_edit} onClose={onClose} onSaved={onSaved} />;
}

function GridEditor({ code, prefix, name, plan, grid, canEdit, onClose, onSaved }: {
  code: string; prefix: string; name: string; plan: PlanRef; grid: Grid | null; canEdit: boolean;
  onClose: () => void; onSaved: () => void;
}) {
  const { doc, err: pdfErr } = useOpenPdf(plan.path);
  const [st, setSt] = useState<State>({ lines: grid?.lines ?? [], zones: grid?.zones ?? [] });
  const saved = useRef(JSON.stringify({ lines: grid?.lines ?? [], zones: grid?.zones ?? [] }));
  const past = useRef<State[]>([]), future = useRef<State[]>([]);
  const [, bump] = useState(0);
  const [sel, setSel] = useState<string | null>(null);
  const [tool, setTool] = useState<Tool>("select");
  const [opts, setOpts] = useState<DetectOptions>(DETECT_DEFAULTS);
  const [ratio, setRatio] = useState<number>(grid?.ratio ?? 0.707);
  const [detecting, setDetecting] = useState(false);
  const [err, setErr] = useState<string | null>(pdfErr);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<[number, number][] | null>(null);   // zone being drawn
  const [adjusting, setAdjusting] = useState<string | null>(null);        // zone whose corners show
  const [preview, setPreview] = useState<Preview | null>(null);
  const [picking, setPicking] = useState(false);
  const [askReplace, setAskReplace] = useState(false);
  const [cursor, setCursor] = useState<{ u: number; v: number } | null>(null);
  useEffect(() => { if (pdfErr) setErr(pdfErr); }, [pdfErr]);

  // ---- history ------------------------------------------------------------------------------------------------------
  const commit = useCallback((next: State | ((s: State) => State)) => {
    setSt((cur) => { const n = typeof next === "function" ? next(cur) : next; if (n !== cur) { past.current.push(cur); future.current = []; } return n; });
    bump((x) => x + 1);
  }, []);
  /** live change during a drag: no history step (the drag start pushed one) */
  const live = (fn: (s: State) => State) => setSt(fn);
  const undo = () => { const p = past.current.pop(); if (!p) return; future.current.push(st); setSt(p); bump((x) => x + 1); };
  const redo = () => { const f = future.current.pop(); if (!f) return; past.current.push(st); setSt(f); bump((x) => x + 1); };
  const dirty = JSON.stringify(st) !== saved.current || !!preview;

  // ---- detection ------------------------------------------------------------------------------------------------------
  const geo = useRef<Promise<PageGeometry> | null>(null);
  const detect = useCallback(async (o: DetectOptions, record: boolean) => {
    if (!doc) return;
    setDetecting(true);
    try {
      geo.current ??= doc.geometry(plan.page, plan.rotation);
      const g = await geo.current;
      setRatio(g.height / g.width);
      const found = detectGrid(g, o);
      if (record) commit((s) => ({ lines: found, zones: s.zones }));
      else { setSt((s) => ({ lines: found, zones: s.zones })); }
      setSel(null);
      if (!found.length) setErr("No gridlines were found on this plan — add them with Add line, or copy them from another component.");
      else setErr(null);
    } catch (e) { setErr(`The plan could not be read: ${(e as Error).message}`); }
    finally { setDetecting(false); }
  }, [doc, plan.page, plan.rotation, commit]);
  // first visit: nothing saved yet → detect straight away (not saved until Save changes)
  const autoRan = useRef(false);
  useEffect(() => { if (doc && !grid && canEdit && !autoRan.current) { autoRan.current = true; detect(opts, false); } }, [doc, grid, canEdit, detect, opts]);

  // ---- view: fit / zoom / turn / pan ------------------------------------------------------------------------------------
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [view, setView] = useState({ zoom: 1, rot: 0, px: 0, py: 0 });
  useEffect(() => {
    const el = box.current; if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight })); ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);
  const BW = Math.max(200, Math.floor(Math.min(size.w - 24, (size.h - 24) / ratio))), BH = BW * ratio;
  const fit = useCallback((rot = view.rot) => {
    const turned = rot % 180 !== 0, w = turned ? BH : BW, h = turned ? BW : BH;
    const zoom = Math.min((size.w - 24) / w, (size.h - 24) / h);
    // centre: the stage's rotated box sits in the middle
    const c = rotate(BW / 2 * zoom, BH / 2 * zoom, rot);
    setView({ zoom, rot, px: size.w / 2 - c[0], py: size.h / 2 - c[1] });
  }, [BW, BH, size.w, size.h, view.rot]);
  const fitted = useRef(false);
  useEffect(() => { if (size.w > 50 && !fitted.current) { fitted.current = true; fit(0); } }, [size.w, fit]);
  useEffect(() => { if (fitted.current) fit(view.rot); }, [BW]); // eslint-disable-line react-hooks/exhaustive-deps
  const zoomAt = (k: number, sx = size.w / 2, sy = size.h / 2) => setView((v) => {
    const zoom = clamp(v.zoom * k, 0.2, 12), r = zoom / v.zoom;
    return { ...v, zoom, px: sx - (sx - v.px) * r, py: sy - (sy - v.py) * r };
  });
  // draw the plan; sharper when zoomed in (after a short pause)
  const drawing = useRef<Promise<unknown>>(Promise.resolve());
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    if (!doc || !canvas.current || BW < 100) return;
    const t = setTimeout(() => {
      drawing.current = drawing.current.then(() => doc.render(plan.page, canvas.current!, BW, Math.min(view.zoom, 6), plan.rotation)
        .then((r) => { setDrawn(true); if (Math.abs(r - ratio) > 0.001) setRatio(r); }).catch(() => {}));
    }, drawn ? 250 : 0);
    return () => clearTimeout(t);
  }, [doc, BW, plan.page, plan.rotation, view.zoom >= 1.5 ? Math.round(view.zoom) : 1]); // eslint-disable-line react-hooks/exhaustive-deps

  /** pointer → page coordinates (0–1 across / down the plan) */
  const toStage = (e: { clientX: number; clientY: number }) => {
    const r = box.current!.getBoundingClientRect();
    const [x, y] = rotate(e.clientX - r.left - view.px, e.clientY - r.top - view.py, -view.rot);
    return { u: x / view.zoom / BW, v: y / view.zoom / BH };
  };

  // ---- editing ------------------------------------------------------------------------------------------------------------
  const lines = st.lines, zones = st.zones;
  const selected = lines.find((l) => l.id === sel) ?? null;
  const counts = gridCounts(lines);
  const setLine = (id: string, patch: Partial<GridLine>, record = true) =>
    (record ? commit : live)((s) => ({ ...s, lines: s.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)) }));
  const removeLine = (id: string) => { commit((s) => ({ ...s, lines: s.lines.filter((l) => l.id !== id) })); setSel(null); };
  const snapAngle = (u1: number, v1: number, u2: number, v2: number, free: boolean) => {
    if (free) return { u2, v2 };
    const ang = lineAngle({ x1: u1, y1: v1, x2: u2, y2: v2 }, ratio);
    const cands = [0, 90, -90, ...lines.map((l) => lineAngle(l, ratio))];
    const best = cands.reduce((b, a) => (Math.abs(a - ang) < Math.abs(b - ang) ? a : b), 999);
    if (Math.abs(best - ang) > 4) return { u2, v2 };
    const len = Math.hypot(u2 - u1, (v2 - v1) * ratio), r = (best * Math.PI) / 180, sgn = Math.sign((u2 - u1) * Math.cos(r) + (v2 - v1) * ratio * Math.sin(r)) || 1;
    return { u2: u1 + Math.cos(r) * len * sgn, v2: v1 + (Math.sin(r) * len * sgn) / ratio };
  };
  const finishZone = (pts: [number, number][]) => {
    if (pts.length < 3) return;
    const n = zones.length + 1;
    const z: GridZone = { id: newId("z"), name: `Zone ${n}`, short: `Z${n}`, points: pts.map(([u, v]) => [round5(u), round5(v)] as [number, number]) };
    const inside = new Set(linesInZone(lines, z).map((l) => l.id));
    commit((s) => ({ zones: [...s.zones, z], lines: s.lines.map((l) => (inside.has(l.id) ? { ...l, zone: z.id } : l)) }));
    setDraft(null); setTool("select"); setAdjusting(null);
  };

  const drag = useRef<Drag | null>(null);
  const onDown = (e: RPointerEvent) => {
    if (e.button !== 0) return;
    if ((e.target as Element).closest?.(".gs-preview, button, input")) return;    // the preview bar's buttons work as buttons
    const p = toStage(e), t = e.target as Element;
    const lineId = t.closest("[data-line]")?.getAttribute("data-line"), endK = t.getAttribute("data-end");
    const corner = t.getAttribute("data-corner");
    box.current?.setPointerCapture?.(e.pointerId);
    if (preview) {
      if (t.closest("[data-copy]")) drag.current = { kind: "copy", u: p.u, v: p.v, dx: preview.dx, dy: preview.dy };
      else drag.current = { kind: "pan", sx: e.clientX, sy: e.clientY, px: view.px, py: view.py };
      return;
    }
    if (!canEdit) { drag.current = { kind: "pan", sx: e.clientX, sy: e.clientY, px: view.px, py: view.py }; return; }
    if (corner && adjusting) { const [zid, k] = corner.split(":"); past.current.push(st); future.current = []; drag.current = { kind: "corner", zone: zid, k: Number(k) }; return; }
    // v070-030: while corners are adjusted, nothing else on the drawing can be picked — only corners move, the rest pans
    if (adjusting) { drag.current = { kind: "pan", sx: e.clientX, sy: e.clientY, px: view.px, py: view.py }; return; }
    if (tool === "zone") {
      const first = draft?.[0];
      if (first && draft!.length >= 3 && Math.hypot((first[0] - p.u) * BW * view.zoom, (first[1] - p.v) * BH * view.zoom) < 10) finishZone(draft!);
      else setDraft([...(draft ?? []), [p.u, p.v]]);
      return;
    }
    if (tool === "add") {
      const id = newId("m");
      commit((s) => ({ ...s, lines: [...s.lines, { id, x1: round5(p.u), y1: round5(p.v), x2: round5(p.u), y2: round5(p.v), label: null, zone: null }] }));
      setSel(id); drag.current = { kind: "new", id, u: p.u, v: p.v }; return;
    }
    if (lineId && endK && lineId === sel) { past.current.push(st); future.current = []; drag.current = { kind: "end", id: lineId, end: Number(endK) as 1 | 2, orig: lines.find((l) => l.id === lineId)! }; return; }
    if (lineId) { setSel(lineId); drag.current = { kind: "move", id: lineId, u: p.u, v: p.v, orig: lines.find((l) => l.id === lineId)!, moved: false }; return; }
    setSel(null);
    drag.current = { kind: "pan", sx: e.clientX, sy: e.clientY, px: view.px, py: view.py };
  };
  const onMove = (e: RPointerEvent) => {
    const p = toStage(e);
    setCursor(p.u >= 0 && p.u <= 1 && p.v >= 0 && p.v <= 1 ? p : null);
    const d = drag.current; if (!d) return;
    if (d.kind === "pan") { setView((v) => ({ ...v, px: d.px + e.clientX - d.sx, py: d.py + e.clientY - d.sy })); return; }
    if (d.kind === "copy") { setPreview((pv) => (pv ? { ...pv, dx: d.dx + p.u - d.u, dy: d.dy + p.v - d.v } : pv)); return; }
    if (d.kind === "new") { const s2 = snapAngle(d.u, d.v, p.u, p.v, e.shiftKey); setLine(d.id, { x2: round5(s2.u2), y2: round5(s2.v2) }, false); return; }
    if (d.kind === "move") {
      if (!d.moved) { past.current.push(st); future.current = []; d.moved = true; }
      const du = p.u - d.u, dv = p.v - d.v, o = d.orig;
      setLine(d.id, { x1: round5(o.x1 + du), y1: round5(o.y1 + dv), x2: round5(o.x2 + du), y2: round5(o.y2 + dv) }, false); return;
    }
    if (d.kind === "end") {
      const o = d.orig;
      if (e.shiftKey) { setLine(d.id, d.end === 1 ? { x1: round5(p.u), y1: round5(p.v) } : { x2: round5(p.u), y2: round5(p.v) }, false); return; }
      // keep the angle: slide the end along the line
      const ux = o.x2 - o.x1, uy = (o.y2 - o.y1) * ratio, len = Math.hypot(ux, uy) || 1, dx = ux / len, dy = uy / len;
      const [ax, ay] = d.end === 1 ? [o.x2, o.y2 * ratio] : [o.x1, o.y1 * ratio];
      const t = (p.u - ax) * dx + (p.v * ratio - ay) * dy;
      const nx = ax + dx * t, ny = (ay + dy * t) / ratio;
      setLine(d.id, d.end === 1 ? { x1: round5(nx), y1: round5(ny) } : { x2: round5(nx), y2: round5(ny) }, false); return;
    }
    if (d.kind === "corner") live((s) => ({ ...s, zones: s.zones.map((z) => (z.id === d.zone ? { ...z, points: z.points.map((q, k) => (k === d.k ? [round5(p.u), round5(p.v)] as [number, number] : q)) } : z)) }));
  };
  const onUp = () => {
    const d = drag.current; drag.current = null;
    if (d?.kind === "new") {
      const l = st.lines.find((x) => x.id === d.id);
      if (l && Math.hypot(l.x2 - l.x1, (l.y2 - l.y1) * ratio) < 0.01) { setSt((s) => ({ ...s, lines: s.lines.filter((x) => x.id !== d.id) })); past.current.pop(); setSel(null); }
    }
  };
  // v070-017: Ctrl + wheel zooms the plan (not the browser); a plain wheel / trackpad pans
  useWheelZoom(box, (k, cx, cy) => { const r = box.current!.getBoundingClientRect(); zoomAt(k, cx - r.left, cy - r.top); });
  const onWheel = (e: React.WheelEvent) => { if (e.ctrlKey || e.metaKey) return; setView((v) => ({ ...v, px: v.px - e.deltaX, py: v.py - e.deltaY })); };

  // keyboard: Delete, Ctrl+Z / Ctrl+Y (not while typing in a field)
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const typing = (e.target as Element)?.closest?.("input, select, textarea");
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !typing) { e.preventDefault(); if (e.shiftKey) redo(); else undo(); }
      else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y" && !typing) { e.preventDefault(); redo(); }
      else if ((e.key === "Delete" || e.key === "Backspace") && !typing && sel && canEdit && !preview && !adjusting) { e.preventDefault(); e.stopPropagation(); removeLine(sel); }
      else if (e.key === "Enter" && tool === "zone" && draft && draft.length >= 3) { e.preventDefault(); finishZone(draft); }
    };
    window.addEventListener("keydown", on, true);
    return () => window.removeEventListener("keydown", on, true);
  });

  // ---- copy (A6.4.1 / A6.4.3) ------------------------------------------------------------------------------------------
  const startCopy = async (from: string) => {
    setPicking(false); setErr(null);
    try {
      const g = await rpc("issues_grid_get", { code, prefix: from });
      if (!g.grid) return setErr(`${from} has no gridlines to copy`);
      const c = copyGrid(g.grid.lines, g.grid.zones);
      setPreview({ ...c, from: { prefix: g.prefix, name: g.name, file: g.plan?.name ?? "" }, dx: 0, dy: 0 });
      setSel(null); setTool("select"); setAdjusting(null);
    } catch (e) { setErr((e as Error).message); }
  };
  const applyCopy = () => {
    if (!preview) return;
    const sh = (l: GridLine) => ({ ...l, x1: round5(l.x1 + preview.dx), y1: round5(l.y1 + preview.dy), x2: round5(l.x2 + preview.dx), y2: round5(l.y2 + preview.dy) });
    const zs = preview.zones.map((z) => ({ ...z, points: z.points.map(([u, v]) => [round5(u + preview.dx), round5(v + preview.dy)] as [number, number]) }));
    commit({ lines: preview.lines.map(sh), zones: zs });
    setPreview(null); setAskReplace(false);
    toast(`${preview.lines.length} gridlines copied from ${preview.from.prefix}`);
  };

  // ---- save / close -----------------------------------------------------------------------------------------------------
  const save = async () => {
    if (preview) return setErr("Confirm or discard the copy first.");
    for (const z of zones) if (!z.name.trim() || !z.short.trim()) return setErr("Every zone needs a name and a short name.");
    setBusy(true); setErr(null);
    try {
      await rpc("issues_grid_save", { code, prefix, lines: st.lines.map(({ id, x1, y1, x2, y2, label, zone, check }) =>
        ({ id, x1, y1, x2, y2, label: label?.trim() || null, zone, ...(check ? { check } : {}) })), zones: st.zones, ratio: Math.round(ratio * 1e5) / 1e5 });
      saved.current = JSON.stringify(st);
      toast(counts.toCheck ? `Gridlines saved — ${counts.toCheck} still to check` : "Gridlines saved");
      onSaved();
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  const ask = useAskClose(dirty && canEdit, onClose, save);
  useEsc("popup", () => {
    if (askReplace) { setAskReplace(false); return true; }
    if (picking) { setPicking(false); return true; }
    if (draft) { setDraft(null); return true; }
    if (tool !== "select") { setTool("select"); return true; }
    if (adjusting) { setAdjusting(null); return true; }
    ask.close(); return true;
  });

  // ---- drawing ------------------------------------------------------------------------------------------------------------
  const z = view.zoom, sw = (w: number) => w / z;
  const X = (u: number) => u * BW, Y = (v: number) => v * BH;
  const reading = cursor ? pinReading(preview ? preview.lines.map((l) => ({ ...l, x1: l.x1 + preview.dx, x2: l.x2 + preview.dx, y1: l.y1 + preview.dy, y2: l.y2 + preview.dy })) : lines,
    preview ? preview.zones : zones, cursor.u, cursor.v, ratio) : null;
  const zoneColour = (id: string | null) => ZONE_COLOURS[Math.max(0, zones.findIndex((x) => x.id === id)) % ZONE_COLOURS.length];
  const lineEl = (l: GridLine, mode: "main" | "ghost" | "copy") => {
    const amber = isAmber(l), on = mode === "main" && l.id === sel;
    const colour = mode === "ghost" ? "#a8a59c" : mode === "copy" ? "#2563EB" : on ? "#B9481F" : amber ? "#EF9F27" : "#3B6D11";
    const r = sw(9);
    return (
      <g key={`${mode}${l.id}`} data-line={mode === "main" ? l.id : undefined} data-copy={mode === "copy" ? "1" : undefined} data-label={l.label ?? ""} data-amber={amber ? "1" : "0"}>
        <line x1={X(l.x1)} y1={Y(l.y1)} x2={X(l.x2)} y2={Y(l.y2)} stroke="transparent" strokeWidth={sw(12)} />
        <line x1={X(l.x1)} y1={Y(l.y1)} x2={X(l.x2)} y2={Y(l.y2)} stroke={colour} strokeWidth={sw(on ? 2.4 : 1.6)} strokeDasharray={mode === "copy" ? `${sw(6)} ${sw(4)}` : undefined} />
        <circle cx={X(l.x1)} cy={Y(l.y1)} r={r} fill={colour} />
        <text x={X(l.x1)} y={Y(l.y1)} dy={sw(3.5)} textAnchor="middle" fontSize={sw(l.label && l.label.length > 2 ? 7 : 9)} fontWeight="600" fill="#fff"
          transform={view.rot ? `rotate(${-view.rot} ${X(l.x1)} ${Y(l.y1)})` : undefined}>{l.label?.trim() || "?"}</text>
        {on && <>{[1, 2].map((k) => <circle key={k} data-end={k} cx={X(k === 1 ? l.x1 : l.x2)} cy={Y(k === 1 ? l.y1 : l.y2)} r={sw(5)} fill="#fff" stroke="#B9481F" strokeWidth={sw(1.6)} className="gs-end" />)}</>}
      </g>);
  };
  const shifted = preview ? preview.lines.map((l) => ({ ...l, x1: l.x1 + preview.dx, x2: l.x2 + preview.dx, y1: l.y1 + preview.dy, y2: l.y2 + preview.dy })) : [];

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && ask.close()}>
      <div className="modal grid-modal" role="dialog" aria-label="Gridline setup">
        <div className="gs-head">
          <h2>Gridline setup</h2>
          <span className="muted small ellipsis" data-testid="gs-plan">{name} ({prefix}) · <Icon name="picture_as_pdf" /> {plan.name} · {plan.version}</span>
          <span className="grow" />
          <button className="btn icon" aria-label="Close" onClick={ask.close}><Icon name="close" /></button>
        </div>
        <div className="gs-toolbar" role="toolbar" aria-label="Gridline tools">
          {(["select", "add", "zone"] as Tool[]).map((t) => (
            <button key={t} className={`btn${tool === t && !preview ? " on" : ""}`} disabled={!canEdit || !!preview || !!adjusting} aria-pressed={tool === t}
              onClick={() => { setTool(t); setDraft(null); if (t !== "select") setSel(null); }}>{t === "select" ? "Select" : t === "add" ? "Add line" : "Draw zone"}</button>))}
          <button className={`btn${preview || picking ? " on" : ""}`} disabled={!canEdit || !!adjusting} onClick={() => setPicking(true)}><Icon name="content_copy" /> Copy from other component</button>
          <span className="v-rule" />
          <button className="btn icon" aria-label="Zoom out" onClick={() => zoomAt(1 / 1.3)}><Icon name="remove" /></button>
          <button className="btn" onClick={() => fit()}>Fit</button>
          <button className="btn icon" aria-label="Zoom in" onClick={() => zoomAt(1.3)}>+</button>
          <span className="v-rule" />
          <button className="btn icon" aria-label="Turn view left" title="Turn view left" onClick={() => fit((view.rot + 270) % 360)}><Icon name="rotate_left" /></button>
          <button className="btn icon" aria-label="Turn view right" title="Turn view right" onClick={() => fit((view.rot + 90) % 360)}><Icon name="rotate_right" /></button>
          <span className="v-rule" />
          <button className="btn" disabled={!past.current.length} onClick={undo}>Undo</button>
          <button className="btn" disabled={!future.current.length} onClick={redo}>Redo</button>
          <span className="grow" />
          <InfoIcon tip={GRID_TIPS.toolbar} />
        </div>
        <div className="gs-body">
          <div className={`gs-canvas tool-${preview ? "copy" : tool}${adjusting ? " adjusting" : ""}`} ref={box} data-testid="gs-canvas" data-drawn={drawn ? "1" : "0"}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={() => setCursor(null)} onWheel={onWheel}
            onDoubleClick={() => { if (tool === "zone" && draft && draft.length >= 3) finishZone(draft); }}>
            <div className="gs-stage" style={{ width: BW, height: BH, transform: `translate(${view.px}px, ${view.py}px) rotate(${view.rot}deg) scale(${z})` }}>
              <canvas ref={canvas} aria-label="Plan" />
              <svg width={BW} height={BH} className="gs-svg" data-testid="gs-svg">
                {zones.map((zn) => (
                  <g key={zn.id} data-zone={zn.short}>
                    <polygon points={zn.points.map(([u, v]) => `${X(u)},${Y(v)}`).join(" ")} fill={zoneColour(zn.id) + "14"} stroke={zoneColour(zn.id)} strokeWidth={sw(1.4)} strokeDasharray={`${sw(6)} ${sw(4)}`} />
                    <text x={X(Math.min(...zn.points.map((p) => p[0])))} y={Y(Math.min(...zn.points.map((p) => p[1])))} dy={sw(-6)} fontSize={sw(11)} fontWeight="600" fill={zoneColour(zn.id)}>{zn.name} · {zn.short}</text>
                    {adjusting === zn.id && zn.points.map(([u, v], k) => <rect key={k} data-corner={`${zn.id}:${k}`} x={X(u) - sw(5)} y={Y(v) - sw(5)} width={sw(10)} height={sw(10)} fill="#fff" stroke="#B9481F" strokeWidth={sw(1.6)} className="gs-corner" />)}
                  </g>))}
                {preview ? <>{lines.map((l) => lineEl(l, "ghost"))}{shifted.map((l) => lineEl(l, "copy"))}</> : lines.map((l) => lineEl(l, "main"))}
                {draft && <polyline points={[...draft, ...(cursor ? [[cursor.u, cursor.v]] : [])].map(([u, v]) => `${X(u)},${Y(v)}`).join(" ")} fill="none" stroke="#3B82F6" strokeWidth={sw(1.6)} strokeDasharray={`${sw(5)} ${sw(4)}`} />}
                {draft?.map(([u, v], k) => <circle key={k} cx={X(u)} cy={Y(v)} r={sw(k === 0 ? 6 : 3.5)} fill={k === 0 ? "#fff" : "#3B82F6"} stroke="#3B82F6" strokeWidth={sw(1.4)} />)}
              </svg>
            </div>
            {!drawn && !err && <div className="gs-loading"><Loading /></div>}
            {detecting && <div className="gs-busy">Detecting gridlines…</div>}
            {cursor && <div className="gs-cursor" data-testid="gs-cursor">{reading ? <>Cursor reads <b>{reading}</b></> : "Cursor: no labelled gridlines here"}</div>}
            {preview && (
              <div className="gs-preview" data-testid="gs-preview">
                <span><b>Preview</b> · {preview.lines.length} gridlines from {preview.from.name} ({preview.from.prefix}){preview.from.file && <> · {preview.from.file}</>}</span>
                <InfoIcon tip={GRID_TIPS.preview} />
                <button className="btn" onClick={() => setPreview(null)}>Discard</button>
                <button className="btn primary blue" onClick={() => (lines.length ? setAskReplace(true) : applyCopy())}>Confirm copy</button>
              </div>)}
          </div>
          <aside className="gs-side">
            {err && <ErrorBox message={err} />}
            <p className="gs-h">Detection</p>
            {preview ? <p className="gs-box muted small">Copy preview on. The grey lines are this plan's current gridlines; blue dashed lines are the copy.</p> : <>
              <div className="gs-box row gap12" data-testid="gs-counts" data-labelled={counts.labelled} data-amber={counts.toCheck}>
                <span className="row gap6"><span className="gs-dot green" /><b>{counts.labelled}</b> labelled</span>
                <span className="row gap6"><span className="gs-dot amber" />{counts.toCheck} to check</span>
              </div>
              <label className="gs-slider"><span className="row gap6">Min length <InfoIcon tip={GRID_TIPS.minLength} /></span>
                <input type="range" min={5} max={60} step={5} value={Math.round(opts.minLength * 100)} disabled={!canEdit} aria-label="Min length"
                  onChange={(e) => setOpts({ ...opts, minLength: Number(e.target.value) / 100 })} /><b>{Math.round(opts.minLength * 100)}%</b></label>
              <label className="gs-slider"><span className="row gap6">Join gaps <InfoIcon tip={GRID_TIPS.joinGap} /></span>
                <input type="range" min={2} max={40} step={1} value={opts.joinGap} disabled={!canEdit} aria-label="Join gaps"
                  onChange={(e) => setOpts({ ...opts, joinGap: Number(e.target.value) })} /><b>{opts.joinGap} pt</b></label>
              <div className="row gap8">
                <button className="btn" disabled={!canEdit || detecting || !doc || !!adjusting} onClick={() => detect(opts, true)}>Re-detect</button>
                <button className="btn" disabled={!canEdit || !counts.toCheck || !!adjusting} onClick={() => { commit((s) => ({ ...s, lines: s.lines.filter((l) => !isAmber(l)) })); setSel(null); }}>Remove amber lines</button>
              </div>
            </>}
            <p className="gs-h">Selected line</p>
            {preview ? <p className="gs-box muted small">Confirm or discard the copy first.</p>
              : !selected ? <p className="gs-box muted small">Click a line on the drawing.</p>
              : <div className="gs-box gs-sel" data-testid="gs-selected">
                  <label className="gs-row"><span>Label</span>
                    <input value={selected.label ?? ""} maxLength={12} disabled={!canEdit} aria-label="Label" placeholder="?"
                      onChange={(e) => setLine(selected.id, { label: e.target.value.toUpperCase() || null, check: false })} />
                    {!selected.label?.trim() ? <span className="amber-text small">label unreadable</span> : selected.check ? <span className="amber-text small">same label twice</span> : null}</label>
                  <label className="gs-row"><span>Zone</span>
                    <select value={selected.zone ?? ""} disabled={!canEdit} aria-label="Zone" onChange={(e) => setLine(selected.id, { zone: e.target.value || null })}>
                      <option value="">No zone</option>{zones.map((zn) => <option key={zn.id} value={zn.id}>{zn.name} · {zn.short}</option>)}</select></label>
                  <label className="gs-row"><span>Angle</span>
                    <AngleInput value={lineAngle(selected, ratio)} disabled={!canEdit} onSet={(a) => commit((s) => ({ ...s, lines: s.lines.map((l) => (l.id === selected.id ? setAngle(l, a, ratio) : l)) }))} /> °</label>
                  <div className="row gap6"><button className="btn" disabled={!canEdit} onClick={() => removeLine(selected.id)}>Delete line</button><InfoIcon tip={GRID_TIPS.deleteLine} /></div>
                </div>}
            <p className="gs-h row gap6">Zones {zones.length > 0 && <InfoIcon tip={GRID_TIPS.zones} />}</p>
            {preview ? <p className="gs-box muted small">{preview.zones.length ? `${preview.zones.length} zone${preview.zones.length > 1 ? "s" : ""} come with the copy.` : `${preview.from.prefix} has no zones to copy.`}</p>
              : !zones.length ? <p className="gs-box muted small row gap6">{tool === "zone" ? "Click the corners on the drawing; click the first corner again to finish." : <>No zones <InfoIcon tip={GRID_TIPS.noZones} /></>}</p>
              : zones.map((zn) => {
                  const own = lines.filter((l) => l.zone === zn.id);
                  return (
                    <div key={zn.id} className="gs-zone" style={{ borderLeftColor: zoneColour(zn.id) }} data-zone-card={zn.short}>
                      <div className="row gap6">
                        <input value={zn.name} maxLength={40} aria-label="Zone name" disabled={!canEdit} onChange={(e) => commit((s) => ({ ...s, zones: s.zones.map((x) => (x.id === zn.id ? { ...x, name: e.target.value } : x)) }))} />
                        <input className="gs-short" value={zn.short} maxLength={4} aria-label="Zone short name" disabled={!canEdit} onChange={(e) => commit((s) => ({ ...s, zones: s.zones.map((x) => (x.id === zn.id ? { ...x, short: e.target.value.toUpperCase() } : x)) }))} />
                        <button className="btn icon quiet" aria-label={`Remove zone ${zn.name}`} disabled={!canEdit}
                          onClick={() => { commit((s) => ({ zones: s.zones.filter((x) => x.id !== zn.id), lines: s.lines.map((l) => (l.zone === zn.id ? { ...l, zone: null } : l)) })); if (adjusting === zn.id) setAdjusting(null); }}><Icon name="close" /></button>
                      </div>
                      <div className="row between"><span className="muted small">{own.length} gridlines · grid angle {gridAngle(own, ratio)}°</span>
                        <button className={`btn${adjusting === zn.id ? " primary" : ""}`} disabled={!canEdit || (!!adjusting && adjusting !== zn.id)} onClick={() => { setAdjusting(adjusting === zn.id ? null : zn.id); setSel(null); }}>{adjusting === zn.id ? "Done" : "Adjust corners"}</button></div>
                    </div>);
                })}
            <span className="grow" />
            {counts.toCheck > 0 && !preview && <p className="amber-text small gs-warn">{counts.toCheck} line{counts.toCheck > 1 ? "s" : ""} still amber — saving now marks the plan as partly set up</p>}
            <div className="row gap12 gs-foot">
              <span className="grow" />
              <button className="btn" onClick={ask.close}>Cancel</button>
              {canEdit && <button className="btn primary" disabled={busy || !dirty} onClick={save}>Save changes</button>}
            </div>
          </aside>
        </div>
        {picking && <CopyPicker code={code} prefix={prefix} onCancel={() => setPicking(false)} onApply={startCopy} />}
        {askReplace && preview && (
          <div className="modal-backdrop top" onMouseDown={(e) => e.target === e.currentTarget && setAskReplace(false)}>
            <div className="modal small" role="alertdialog" aria-label="Replace existing gridlines?">
              <h2 className="row gap6">Replace existing gridlines? <InfoIcon tip={GRID_TIPS.replace} /></h2>
              <p className="muted">{name} ({prefix}) already has <b>{lines.length} gridlines</b>. They will be replaced by the {preview.lines.length} gridlines copied from {preview.from.name} ({preview.from.prefix}).</p>
              <div className="modal-foot"><button className="btn" onClick={() => { setAskReplace(false); setPreview(null); }}>Keep existing</button>
                <button className="btn primary" onClick={applyCopy}>Replace gridlines</button></div>
            </div>
          </div>)}
        {ask.confirm}
      </div>
    </div>
  );
}

function AngleInput({ value, disabled, onSet }: { value: number; disabled: boolean; onSet: (a: number) => void }) {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);
  const go = () => { const a = Number(text); if (Number.isFinite(a) && a !== value) onSet(clamp(a, -90, 90)); else setText(String(value)); };
  return <input className="gs-angle" value={text} disabled={disabled} aria-label="Angle" inputMode="decimal"
    onChange={(e) => setText(e.target.value)} onBlur={go} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); go(); } }} />;
}

/** A6.4.1 — which component to copy from. Only finished grids can be picked; this component and plan-less ones are greyed. */
function CopyPicker({ code, prefix, onCancel, onApply }: { code: string; prefix: string; onCancel: () => void; onApply: (prefix: string) => void }) {
  const q = useRpc("plans_get", { code });
  const [pick, setPick] = useState<string | null>(null);
  const rows = useMemo(() => (q.data as PlansInfo | null)?.components ?? [], [q.data]);
  return (
    <div className="modal-backdrop top" onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="modal copy-picker" role="dialog" aria-label="Copy gridlines from another component">
        <div className="modal-head"><h2>Copy gridlines from another component</h2><button className="btn icon" aria-label="Close" onClick={onCancel}><Icon name="close" /></button></div>
        <p className="muted small row gap6">Which component would you like to copy from? <InfoIcon tip={GRID_TIPS.copyFrom} /></p>
        {q.error && <ErrorBox message={q.error} />}
        {!q.data ? <Loading /> : (
          <div className="copy-list" role="radiogroup" aria-label="Components">
            {rows.map((c) => {
              const self = c.prefix === prefix, ok = !self && c.grid.status === "done";
              const note = self ? "this component" : !c.current ? "no plan yet" : c.grid.status === "none" ? "no gridlines yet" : c.grid.status === "partial" ? "grid not finished"
                : `${c.grid.lines} gridlines · ${c.grid.zones ? `${c.grid.zones} zone${c.grid.zones > 1 ? "s" : ""}` : "no zones"}`;
              return (
                <label key={c.prefix} className={`copy-row${ok ? "" : " off"}${pick === c.prefix ? " on" : ""}`} data-copy-from={c.prefix}>
                  {ok ? <input type="radio" name="copy-from" checked={pick === c.prefix} onChange={() => setPick(c.prefix)} aria-label={`${c.prefix} ${c.name}`} /> : <span className="copy-radio-gap" />}
                  <b className="code">{c.prefix}</b><span className="ellipsis grow">{c.name}</span>
                  <span className="muted small">{note}</span>
                  <span className={c.current ? "plan-ver static" : "plan-none static"}>{c.current?.version ?? "—"}</span>
                  <GridBadge s={c.grid} hasPlan={!!c.current} />
                </label>);
            })}
          </div>)}
        <div className="modal-foot"><button className="btn" onClick={onCancel}>Cancel</button><button className="btn primary" disabled={!pick} onClick={() => pick && onApply(pick)}>Apply</button></div>
      </div>
    </div>
  );
}

/** A6 Grid column badge: green ✓ = done; orange ring with % = partly set up; dashed ⇪ = no plan; empty ring = not started. */
export function GridBadge({ s, hasPlan }: { s: { status: string; percent: number }; hasPlan: boolean }) {
  if (!hasPlan) return <span className="grid-badge none" aria-label="No plan yet"><Icon name="file_upload" /></span>;
  if (s.status === "done") return <span className="grid-badge done" aria-label="Grid set up"><Icon name="check" /></span>;
  const p = s.status === "partial" ? s.percent : 0;
  return (
    <span className="grid-badge ring" aria-label={s.status === "partial" ? `Grid ${p}% set up` : "Grid not set up"} style={{ background: `conic-gradient(#EF9F27 ${p * 3.6}deg, #f0ede6 0deg)` }}>
      <span>{s.status === "partial" ? `${p}%` : ""}</span>
    </span>);
}

function rotate(x: number, y: number, deg: number): [number, number] {
  const r = (deg * Math.PI) / 180, c = Math.round(Math.cos(r) * 1e9) / 1e9, s = Math.round(Math.sin(r) * 1e9) / 1e9;
  return [x * c - y * s, x * s + y * c];
}
const round5 = (v: number) => Math.round(v * 1e5) / 1e5;
